import React, { useEffect, useState, useContext } from 'react';
import api from '../../api';
import { AuthContext } from '../../context/AuthContext';
import { useDeleteMode } from '../../context/DeleteModeContext';
import SocialActions from '../SocialActions';
import CommentModal from '../CommentModal';
import FullscreenMediaModal from '../FullscreenMediaModal';
import LoadingSkeleton, { LoadError } from '../LoadingSkeleton';
import ShareModal from '../ShareModal';
import useDeepLink from '../../utils/useDeepLink';

const DoodleGallery = ({ setShowDoodleModal, onOpenDoodleMaker }) => {
  const { user } = useContext(AuthContext);
  const { deleteMode } = useDeleteMode();
  const [doodles, setDoodles] = useState([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [selectedDoodle, setSelectedDoodle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);
  const [popular, setPopular] = useState(null);

  useDeepLink('doodle', doodles, loading);

  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [commentContent, setCommentContent] = useState(null);
  const [commentContentType, setCommentContentType] = useState('');

  const fetchDoodles = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get('/doodles', { params: { search, sort } });
      setDoodles(res.data);
      // If commentModal is open, update commentContent with fresh data
      if (commentModalOpen && commentContent) {
        const fresh = res.data.find(d => d.id === commentContent.id);
        if (fresh) setCommentContent(fresh);
      }
    } catch (err) {
      console.error('Failed to fetch doodles:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoodles();
  }, [search, sort]);

  const openShare = async (doodle) => {
    setShareTarget(doodle);
    if (popular === null) {
      try {
        const res = await api.get('/doodles', { params: { sort: 'likes' } });
        const top = res.data && res.data[0];
        setPopular(top && top.id !== doodle.id ? { contentType: 'doodle', id: top.id, title: top.title } : false);
      } catch (err) {
        setPopular(false);
      }
    }
  };

  // Applied in place: refetching the gallery on a like is what made liking
  // feel like a page refresh.
  const applyLike = (id, liked) => {
    setDoodles((prev) => prev.map((d) => (
      d.id === id ? { ...d, isLiked: liked, likes: liked ? (d.likes || 0) + 1 : Math.max(0, (d.likes || 0) - 1) } : d
    )));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this doodle?')) return;
    try {
      await api.delete(`/doodles/${id}`);
      // Remove in place — no full refetch, so the gallery updates instantly.
      setDoodles((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      alert('Failed to delete doodle');
    }
  };

  const handleComment = async (contentId, text) => {
    try {
      await api.post(`/doodles/${contentId}/comment`, { text });
      await fetchDoodles(); // re-fetch and update commentContent via fetchDoodles logic
    } catch (err) {
      alert('Error posting comment');
    }
  };

  const openCommentModal = (item) => {
    setCommentContent(item);
    setCommentContentType('doodle');
    setCommentModalOpen(true);
  };

  return (
    <div className="panel active">
      <div className="section-header" style={{ background: 'linear-gradient(135deg, #00cc66, #00ff99)' }}>
        <span className="section-icon">🎨</span>
        <h2>DOODLE GALLERY</h2>
        <p>Where art meets condiments</p>
      </div>

      <div className="doodle-actions">
        <button type="button" className="btn btn-warning" onClick={onOpenDoodleMaker}>
          <i className="fas fa-paintbrush"></i> MAKE DOODLE
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={fetchDoodles}
          disabled={loading}
          title="Reload the gallery"
        >
          <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync'}`}></i> REFRESH
        </button>
        <button type="button" className="btn btn-success" onClick={() => setShowDoodleModal(true)}>
          <i className="fas fa-upload"></i> ADD DOODLE
        </button>
      </div>

      {/* ─── Search & Sort ─── */}
      <div className="flex justify-between align-center mb-20 sort-bar">
        <input
          type="text"
          placeholder="Search doodles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="form-control search-input"
        />
        <div className="flex gap-10 sort-options">
          <button className="btn btn-secondary" onClick={() => setSort('likes')}>MOST LIKED</button>
          <button className="btn btn-secondary" onClick={() => setSort('newest')}>NEWEST</button>
          <button className="btn btn-secondary" onClick={() => setSort('oldest')}>OLDEST</button>
        </div>
      </div>

      {loading ? (
        <LoadingSkeleton variant="tile" count={6} gridClass="grid-3" message="Loading doodles…" />
      ) : error ? (
        <LoadError message="Couldn't load the doodles." onRetry={fetchDoodles} />
      ) : doodles.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">🎨</span>
          <p>No doodles yet.</p>
        </div>
      ) : (
        <div className="grid-3">
          {doodles.map(d => (
            <div className="doodle-card" key={d.id} id={`doodle-${d.id}`}>
              {d.image_url && d.image_url.startsWith('http') ? (
                <img
                  src={d.image_url}
                  alt={d.title}
                  style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain', cursor: 'pointer' }}
                  onClick={() => setSelectedDoodle(d)}
                />
              ) : (
                <span className="doodle-art" style={{ fontSize: 60, cursor: 'pointer' }} onClick={() => setSelectedDoodle(d)}>
                  {d.image_url || '🎨'}
                </span>
              )}
              <div className="card-title">{d.title}</div>
              {deleteMode && (
                <button className="btn btn-danger btn-sm mt-20" onClick={() => handleDelete(d.id)}>
                  <i className="fas fa-trash"></i> DELETE
                </button>
              )}
              <button className="btn btn-primary btn-sm" onClick={() => setSelectedDoodle(d)}>
                <i className="fas fa-expand"></i> Expand
              </button>
              <SocialActions
                contentType="doodle"
                contentId={d.id}
                likes={d.likes || 0}
                isLiked={!!d.isLiked}
                shares={d.shares || 0}
                commentCount={d.comments ? d.comments.length : 0}
                currentUser={user}
                onUpdate={(liked) => applyLike(d.id, liked)}
                onOpenCommentModal={() => openCommentModal(d)}
                onShare={() => openShare(d)}
              />
            </div>
          ))}
        </div>
      )}

      {selectedDoodle && (
        <FullscreenMediaModal
          isOpen={!!selectedDoodle}
          onClose={() => setSelectedDoodle(null)}
          title={selectedDoodle.title}
          imageUrl={selectedDoodle.image_url && selectedDoodle.image_url.startsWith('http') ? selectedDoodle.image_url : null}
          type="image"
        />
      )}

      <CommentModal
        isOpen={commentModalOpen}
        onClose={() => setCommentModalOpen(false)}
        content={commentContent}
        contentType={commentContentType}
        currentUser={user}
        onComment={handleComment}
      />

      <ShareModal
        open={!!shareTarget}
        onClose={() => setShareTarget(null)}
        contentType="doodle"
        contentId={shareTarget && shareTarget.id}
        title={shareTarget && shareTarget.title}
        subtitle="Fan art from the condiment universe"
        popular={popular || null}
      />
    </div>
  );
};

export default DoodleGallery;